import { TooltipPortal } from '@radix-ui/react-tooltip';
import { ArrowRightUp } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import type { ToOptions } from '@tanstack/router-core';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useDelayedState } from 'src/utils/useDelayedState';
import { Link2 } from './Link2';
import { TooltipContent, TooltipRoot, TooltipTrigger } from './Tooltip';

export function SidebarLink({
  icon: Icon,
  label,
  className,
  expanded = false,
  showArrow = false,
  openInNewTab = false,
  transitionDelay = 100,
  ...props
}: ToOptions & {
  icon?: React.FC<{ className?: string }>;
  label: ReactNode;
  className?: string;
  expanded: boolean;
  showArrow?: boolean;
  openInNewTab?: boolean;
  transitionDelay?: number;
}) {
  const [isHidden, setIsHidden] = useDelayedState(!expanded);

  useEffect(() => {
    if (expanded) {
      setIsHidden(false);
    } else {
      setIsHidden(true, transitionDelay); // Delay hiding the element until after the collapse animation
    }
  }, [expanded, setIsHidden]);

  return (
    <TooltipRoot open={expanded ? false : undefined}>
      <Link2
        className={clsx(
          'group !h-48 w-full rounded font-medium no-underline transition-colors hover:bg-paper-4 data-[status=active]:bg-paper-6',
          {
            hidden: isHidden && !Icon,
          },
          className
        )}
        componentLocation={ComponentLocations.MENU.SIDE_MENU}
        target={openInNewTab ? '_blank' : undefined}
        {...props}
      >
        {() => (
          <TooltipTrigger asChild>
            <span className="flex w-full items-center gap-4 px-8 py-16">
              {Icon && (
                <Icon className={clsx('mx-[5.5px] !size-20 shrink-0')} />
              )}
              <Typography
                variant="interface-md"
                as="span"
                className={clsx(
                  'h-16 w-full overflow-x-clip font-regular whitespace-nowrap transition-all group-data-[status=active]:font-medium',
                  {
                    'opacity-0': !expanded,
                    'opacity-100': expanded,
                    'pl-8': !Icon,
                  }
                )}
              >
                {label}
              </Typography>
              {showArrow && (
                <ArrowRightUp
                  className={clsx('!h-20 shrink-0 transition-all', {
                    'opacity-0': !expanded,
                    'opacity-100': expanded,
                  })}
                />
              )}
            </span>
          </TooltipTrigger>
        )}
      </Link2>
      <TooltipPortal>
        <TooltipContent side="right" sideOffset={16}>
          {label}
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  );
}
